import { useEffect, useMemo, useState } from "react";
import './Catalogs.scss';
import { Nav, Tab } from "react-bootstrap";
import ConfirmationDialog from "../CommonComponents/confirmation_dialog/ConfirmationDialog";
import Payload from "./Tabs/Payload";
import Edge from "./Tabs/Edge";
import BusComponents from "./Tabs/BusComponents";
import Bus from "./Tabs/Bus";
import CreateCatalog from "./Modals/CreateCatalog";
import CreateBus from "./Modals/CreateBus";
import { BASIC_BUS_DATA_FIELDS, BUS_COMPONENTS_TYPES, CatalogConstants } from "./utils/constants";
import CatalogueService from "../../service/CatalogueService";
import { showToastMessage } from "../../utils/commonFunctions";
import Loader from "../../Layouts/Loader/Loader";
import { getEditedBusFormData, getEditedFormData, getNewBusData, getUpdatedFormData } from "./utils";
import { useDispatch, useSelector } from "react-redux";
import { setCloneTemplate } from "../../store/CatalogSlice";
import CloneCatalog from "./Modals/CloneCatalog";
import MissionService from "../../service/MissionService";
import { useParams } from "react-router-dom";

export type BusFormDataType = {
  name: string;
  maxPower: string;
  size: {
    breadth: string;
    width: string;
    height: string;
  },
  inertia: {
    "Ixx": string;
    "Ixy": string;
    "Ixz": string;
    "Iyy": string;
    "Iyz": string;
    "Izz": string;
  },
  maxMass: string;
  maxPayloadSize: string;
  maxPayloadPower: string;
  maxPayloadMass: string;
  simulationParameters: { [key: string]: any } | null; // to hold parsed JSON
}

const Catalogs = () => {
  const { mission_id } = useParams()
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.mission.userData);
  const [catalogeLoaders, setCatalogeLoaders] = useState<{
    Payload: boolean,
    Edge: boolean,
    Bus: boolean,
    'Bus Components': boolean,
    Satellites: boolean
  }>({
    Payload: true,
    Edge: true,
    Bus: true,
    'Bus Components': true,
    Satellites: true
  });
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [refreshCatalog, setRefreshCatalog] = useState<{
    Payload: boolean,
    Edge: boolean,
    Bus: boolean,
    'Bus Components': boolean,
    Satellites: boolean
  }>({
    Payload: true,
    Edge: true,
    Bus: true,
    'Bus Components': true,
    Satellites: true
  });
  const [deleteCatalouge, setDeleteCatalouge] = useState<any>(null);
  const [selectedCatalog, setSelectedCatalog] = useState<any>(null);
  const [formData, setFormData] = useState<any>({
    name: '',
    size: '',
    mass: '',
    peakPower: '',
    averagePower: '',
    typeSpecificParams: {},
  });

  const [busData, setBusData] = useState<BusFormDataType>(BASIC_BUS_DATA_FIELDS);


  const [additionalConfigurations, setAdditionalConfigurations] = useState<{ [key: string]: string }[]>([]);

  const [allSatellites, setAllSatellites] = useState<{
    Payloads: string[],
    Edges: string[],
    Templates: string[]
  }>({
    Payloads: [],
    Edges: [],
    Templates: []
  });
  const [allPayloadDevices, setAllPayloadDevices] = useState<any[]>([]);
  const [allEdgeDevices, setAllEdgeDevices] = useState<any[]>([]);
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [allBusComponents, setAllBusComponents] = useState<any[]>([]);
  const [openConfigurationModal, setOpenConfigurationModal] = useState<'Create' | 'Update' | 'Clone' | null>(null);
  const [tab_name, setTabName] = useState<string>(CatalogConstants.Payload);
  const [importLoader, setImportLoader] = useState<boolean>(false);
  const [modalLoader, setModalLoader] = useState<boolean>(false);

  const Loading = useMemo(() => Object.values(catalogeLoaders).some(item => item), [catalogeLoaders])

  const OpenCloneModal = useMemo(() => openConfigurationModal === 'Clone', [openConfigurationModal])
  const OpenOtherModal = useMemo(() => openConfigurationModal === 'Create' || openConfigurationModal === 'Update', [openConfigurationModal])



  const resetDefaultStates = (section: string) => {
    setRefreshCatalog((prev) => ({
      ...prev,
      [section]: false
    }))
    setCatalogeLoaders((prev) => ({
      ...prev,
      [section]: false
    }))
    setBusData(BASIC_BUS_DATA_FIELDS)
    setModalLoader(false);
    setDeleteLoader(false);
    setImportLoader(false);
    setDeleteCatalouge(null)
    setOpenConfigurationModal(null);
  }

  const getAllSatellitesData = async () => {
    if (!refreshCatalog.Satellites) return
    try {
      const response = await MissionService.getMissionSatInfo(mission_id);
      const satellites = response?.data?.['satelliteGroup'] || [];
      const payloads: string[] = satellites.filter(sat => sat?.payloads).map((satellite: any) => satellite?.payloads.map(payload => payload?.['atmosData']?.['uniqueId']));
      const edges: string[] = satellites.filter(sat => sat?.edges).map((satellite: any) => satellite.edges.map(edge => edge?.['edge-device']?.['atmos-data']?.['uniqueId']));
      const templates: string[] = satellites.filter(sat => sat?.['satelliteGroup']?.['templateId']).map((satellite: any) => satellite?.['satelliteGroup']?.['templateId']);
      const sat_data = {
        Payloads: payloads.flat(),
        Edges: edges.flat(),
        Templates: templates.flat()
      }
      setAllSatellites(sat_data);
    } catch (error) {
      console.error("Error fetching catalogue:", error);
    } finally {
      resetDefaultStates('Satellites')
    }
  }

  const getAllPayloadDevices = async () => {
    if (!refreshCatalog.Payload) return
    try {
      const response = await CatalogueService.getCatalogue("Payload Server", "Payload");
      setAllPayloadDevices(response?.data || []);
    } catch (error) {
      console.error("Error fetching catalogue:", error);
    } finally {
      resetDefaultStates('Payload')
    }
  }

  const getallEdgeDevices = async () => {
    if (!refreshCatalog.Edge) return
    try {
      const response = await CatalogueService.getCatalogue('Edge', 'Edge Node')
      setAllEdgeDevices(response?.data || []);
    } catch (error) {
      console.error("Error fetching catalogue:", error);
    } finally {
      resetDefaultStates('Edge')
    }
  }

  const getAllBusComponents = async () => {
    if (!refreshCatalog?.['Bus Components']) return
    try {
      const response = await CatalogueService.getAllCatalogue()
      const data = response.data;
      const COMPONENT_TYPES = BUS_COMPONENTS_TYPES.map((item) => item.value);
      if (Array.isArray(data)) {
        const filtered = data.filter(
          (item) => COMPONENT_TYPES.includes(item["catalogue-type"])
        );
        setAllBusComponents(filtered);
      }
    } catch (error) {
      console.error("Error fetching catalogue:", error);
    } finally {
      resetDefaultStates('Bus Components')
    }
  }

  const getTemplates = async () => {
    if (!refreshCatalog.Bus) return
    try {
      const response = await CatalogueService.getAllTemplates();
      const templates = response.data || []
      setAllTemplates(templates);
    } catch (error) {
      console.error("Error fetching catalogue:", error);
    } finally {
      resetDefaultStates('Bus')
    }
  }


  const handleImportTemplate = (action?: string, templateId?: string) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';

    fileInput.onchange = (event: any) => {
      if (event.target && event.target.files && event.target.files[0]) {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'application/json') {
          if (action === "bus_update" && templateId) {
            setCatalogeLoaders((prev) => ({
              ...prev, [tab_name]: true
            }))
          } else {
            setImportLoader(true);
          }
          try {
            const reader = new FileReader();
            reader.onload = async (e) => {
              const fileContent = e.target?.result as string;
              try {
                // Parse the JSON data
                const blob = new Blob([fileContent], { type: "multipart/form-data" });
                const file_data = new FormData();
                file_data.append("file", blob, selectedFile.name);
                if (action === "bus_update" && templateId) {
                  // AT-10150 FV-25.3 : API call to Update custom bus template
                  await CatalogueService.putBusTemplate(templateId, file_data);
                } else if (action === "bus_import") {
                  await CatalogueService.importBusTemplate(file_data)
                }
                setRefreshCatalog((prev => ({
                  ...prev,
                  [tab_name]: true
                })));
              } catch (error) {
                showToastMessage('error', "Failed to import bus template");
                setImportLoader(false);
              }
            };
            reader.readAsText(selectedFile);
          } catch (error) {
            showToastMessage('error', "File format is not supported");
            setImportLoader(false);
            setCatalogeLoaders((prev) => ({
              ...prev, [tab_name]: false
            }))
          }
        } else {
          showToastMessage('error', "File format is not supported");
        }
      }
    };

    fileInput.click();
  }

  const deleteUserCatalouge = async (catalogue: any) => {
    const catalogueId = catalogue?.['uniqueId'];
    setDeleteLoader(true);
    try {
      if (tab_name === CatalogConstants.Bus) {
        await CatalogueService.deleteTemplate(catalogueId)
      } else {
        await CatalogueService.deleteCatalogue(catalogueId);
      }
      setRefreshCatalog((prev => ({
        ...prev,
        [tab_name]: true
      })));
      showToastMessage('success', 'Catalogue deleted successfully');
    } catch (error: any) {
      console.error(error);
      setDeleteCatalouge(null)
      setDeleteLoader(false);
      const message = typeof error?.response?.data === 'string' ? error?.response?.data : error?.response?.data?.message || 'Something went wrong while deleting the catalogue';
      showToastMessage('error', message);
    }
  }

  const onEditCatalog = (catalog: any, catalogId: string) => {
    const formData = getEditedFormData(catalog, tab_name);
    setFormData(formData.data);
    setAdditionalConfigurations(formData.additionalConfig || []);
    setSelectedCatalog({
      ...formData,
      uniqueId: catalogId
    })
    setOpenConfigurationModal('Update');
  }

  const onEditTemplate = (template: any, templateId: string) => {
    const { formData, updatedTemplate } = getEditedBusFormData(template);
    setSelectedCatalog({
      ...formData,
      uniqueId: templateId
    })
    setBusData(formData);
    dispatch(setCloneTemplate({
      'All': updatedTemplate
    }))
    setOpenConfigurationModal('Update');
  }

  const onClone = (data: any) => {
    if (tab_name === CatalogConstants.Bus) {
      const name = data?.name.includes('Clone') ? data?.name : `${data?.name} Clone`;
      const serverData = {
        'assembly': data?.assembly,
        'build_info': data?.['build_info'],
        'container-project': data?.['container-project'],
        'latest-version-id': data?.['latest-version-id'],
        'name': name,
        'template-attribute': data?.['template-attribute'],
        'owner-org': userData?.['owner_org'],
      }
      setSelectedCatalog(serverData)
    } else {
      const formData = getEditedFormData(data, tab_name);
      const serverData = getUpdatedFormData(formData?.additionalConfig || [], formData?.data, tab_name);
      setSelectedCatalog(serverData)
    }
    setOpenConfigurationModal('Clone');
  }

  const onCreate = () => {
    setOpenConfigurationModal('Create');
    setFormData({
      name: '',
      size: '',
      mass: '',
      peakPower: '',
      averagePower: '',
      typeSpecificParams: {},
    });
    setAdditionalConfigurations([]);
    setSelectedCatalog(null);
  }

  useEffect(() => {
    getAllSatellitesData()
  }, []);
  useEffect(() => {
    getAllPayloadDevices()
  }, [refreshCatalog.Payload]);
  useEffect(() => {
    getallEdgeDevices()
  }, [refreshCatalog.Edge]);
  useEffect(() => {
    getAllBusComponents();
  }, [refreshCatalog?.['Bus Components']]);
  useEffect(() => {
    getTemplates();
  }, [refreshCatalog?.['Bus']]);


  return (
    <div className="h-100 w-100">
      {Loading && <Loader />}
      <Tab.Container id="left-tabs-example" defaultActiveKey={tab_name}>
        <Nav
          variant="pills"
          className="panel-tabs nav-tabs cat-nav-tabs pt-4 px-4 m-0"
        >
          <Nav.Item>
            <Nav.Link eventKey={CatalogConstants.Payload} active={tab_name === CatalogConstants.Payload}
              onClick={() => { setTabName(CatalogConstants.Payload) }}
            >
              <span id="payload-label">
                Payload
              </span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={CatalogConstants.Edge} active={tab_name === CatalogConstants.Edge}
              onClick={() => { setTabName(CatalogConstants.Edge) }}
            >
              <span id="edge-label">
                Edge
              </span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={CatalogConstants.BusComponents}
              active={tab_name === CatalogConstants.BusComponents}
              onClick={() => { setTabName(CatalogConstants.BusComponents) }}
            >
              <span id="bus-components-label">Bus Components</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={CatalogConstants.Bus}
              active={tab_name === CatalogConstants.Bus}
              onClick={() => { setTabName(CatalogConstants.Bus) }}
            >
              Bus
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="payload" active={tab_name === CatalogConstants.Payload}>
            <Payload
              onEdit={onEditCatalog}
              onClone={onClone}
              allPayloadDevices={allPayloadDevices}
              usedPayloads={allSatellites?.Payloads || []}
              setDeleteCatalouge={setDeleteCatalouge}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="edge" active={tab_name === CatalogConstants.Edge}>
            <Edge
              onEdit={onEditCatalog}
              onClone={onClone}
              allEdgeDevices={allEdgeDevices}
              usedEdges={allSatellites?.Edges || []}
              setDeleteCatalouge={setDeleteCatalouge}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="bus-components" active={tab_name === CatalogConstants.BusComponents}>
            <BusComponents
              onEdit={onEditCatalog}
              onClone={onClone}
              allBusComponents={allBusComponents}
              setDeleteCatalouge={setDeleteCatalouge}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="bus" active={tab_name === CatalogConstants.Bus}>
            <Bus
              onClone={onClone}
              onEdit={onEditTemplate}
              onUpdate={handleImportTemplate}
              templateList={allTemplates}
              usedTemplates={allSatellites?.Templates || []}
              setDeleteCatalouge={setDeleteCatalouge}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
      <div className="catalog-actions">
        <button className="w-100 catalog-btn" onClick={onCreate}>
          Add New
        </button>
        {tab_name === CatalogConstants.Bus &&
          <button className="w-100 catalog-btn" onClick={() => { handleImportTemplate('bus_import') }} disabled={importLoader}>
            {!importLoader && <i className='fe fe-upload'></i>}
            {importLoader && <div className='spin__loader' />}
            <span>Import</span>
          </button>
        }
      </div>


      {openConfigurationModal &&
        <div className="create-modal d-flex align-items-center justify-content-center">
          {(OpenOtherModal && tab_name !== CatalogConstants.Bus) &&
            <CreateCatalog
              formData={formData}
              mode={openConfigurationModal}
              componentType={tab_name}
              setFormData={setFormData}
              editedCatalogData={selectedCatalog}
              setOpenConfigurationModal={setOpenConfigurationModal}
              additionalConfigurations={additionalConfigurations}
              setAdditionalConfigurations={setAdditionalConfigurations}
              setRefreshCatalog={() => setRefreshCatalog((prev) => ({ ...prev, [tab_name]: true }))}
              modalLoader={modalLoader}
              setModalLoader={setModalLoader}
            />
          }
          {(OpenOtherModal && tab_name == CatalogConstants.Bus) &&
            <CreateBus
              mode={openConfigurationModal}
              componentType={tab_name}
              allCatalog={allBusComponents}
              editedTemplateData={selectedCatalog}
              busData={busData}
              setBusData={setBusData}
              blankTemplateData={allTemplates?.find((t) => t["name"] === "Blank")}
              setOpenConfigurationModal={setOpenConfigurationModal}
              setRefreshCatalog={() => setRefreshCatalog((prev) => ({ ...prev, [tab_name]: true }))}
              modalLoader={modalLoader}
              setModalLoader={setModalLoader}
            />
          }
          {OpenCloneModal &&
            <CloneCatalog
              data={selectedCatalog}
              name={selectedCatalog?.name}
              onChangeName={(value: string) => setSelectedCatalog((prev) => prev ? { ...prev, name: value } : prev)}
              componentType={tab_name}
              setOpenConfigurationModal={setOpenConfigurationModal}
              setRefreshCatalog={() => setRefreshCatalog((prev) => ({ ...prev, [tab_name]: true }))}
              modalLoader={modalLoader}
              setModalLoader={setModalLoader}
            />
          }
        </div>
      }

      <ConfirmationDialog
        type='delete'
        loading={deleteLoader}
        title={`Delete ${tab_name} ?`}
        open={deleteCatalouge ? true : false}
        description={`Deleting ${deleteCatalouge?.["name"]} will permanently remove it from the database, and it cannot be restored once deleted.`}
        onCancel={() => {
          setDeleteCatalouge(null)
        }}
        onConfirm={() => {
          deleteUserCatalouge(deleteCatalouge)
        }}
        confirm_button_text={`Delete`}
      />
    </div>
  );
};

export default Catalogs;